import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TeamService } from '../../../../core/services/team.service';
import { Team } from '../../../../core/models/models';
import { TeamDialogComponent } from '../dialog/team-dialog.component';
import { TeamMembersDialogComponent } from '../dialog/team-members-dialog.component';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-team-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatPaginatorModule],
  templateUrl: './team-list.component.html',
  styleUrl: './team-list.component.scss'
})
export class TeamListComponent implements OnInit {
  teams: Team[] = [];
  errorMessage = '';
  successMessage = '';
  showArchived = false;
  searchQuery = '';
  page = signal(0);
  size = signal(10);
  totalElements = signal(0);
  sortField = 'name';
  sortDirection = 'asc';

  private searchSubject = new Subject<string>();

  constructor(
    private teamService: TeamService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTeams();
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadTeams();
    });
  }

  loadTeams(): void {
    this.teamService.getTeams(
      this.searchQuery,
      this.page(),
      this.size(),
      `${this.sortField},${this.sortDirection}`,
      this.showArchived
    ).subscribe({
      next: res => {
        this.teams = res.content;
        this.totalElements.set(res.totalElements);
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement des équipes';
        this.cdr.detectChanges();
      }
    });
  }

  openCreateDialog(): void {
    this.dialog.open(TeamDialogComponent, { width: '560px', data: {} }).afterClosed().subscribe(ok => {
      if (ok) {
        this.page.set(0);
        this.loadTeams();
      }
    });
  }

  openEditDialog(team: Team): void {
    if (team.archived) {
      this.errorMessage = 'Cette équipe est archivée et ne peut pas être modifiée.';
      this.cdr.detectChanges();
      return;
    }
    this.dialog.open(TeamDialogComponent, { width: '560px', data: { ...team } }).afterClosed().subscribe(ok => {
      if (ok) {
        this.loadTeams();
      }
    });
  }

  openMembersDialog(team: Team): void {
    if (team.archived) {
      this.errorMessage = 'Cette équipe est archivée. Gestion des membres indisponible.';
      this.cdr.detectChanges();
      return;
    }
    this.dialog.open(TeamMembersDialogComponent, { width: '960px', data: team }).afterClosed().subscribe(ok => {
      if (ok) {
        this.loadTeams();
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onToggleArchived(): void {
    this.showArchived = !this.showArchived;
    this.page.set(0);
    this.loadTeams();
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadTeams();
  }
}
